/**
 * Invitation rules on the per-tenant invite dialogs (H §6.2): an invitation to the signed-in
 * member's own email is blocked client-side and answered `400 INVITATION_SELF` by tmm-api; an
 * invitee who can't be invited here is answered `403 RESELLER_MEMBER_NOT_INVITABLE` (saas-auth
 * returns it in several cases since SASATH-0342). Both render as a field error on the email.
 */
import type { TFunction } from 'i18next';
import { backendCode } from '@/api/errors';

/**
 * A plausible address, for the client check on an email field (TMMRSL-0071): something, `@`,
 * something, a dot, something. The server decides what is deliverable.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** True when `email` is the signed-in member's own address (case- and space-insensitive). */
export function isSelfInvite(email: string, ownEmail: string | null | undefined): boolean {
  const own = ownEmail?.trim().toLowerCase();
  return Boolean(own) && email.trim().toLowerCase() === own;
}

/** The email field error for an invitation rejection, or `null` when it is not one of these. */
export function inviteEmailError(err: unknown, t: TFunction): string | null {
  const code = backendCode(err);
  if (code === 'INVITATION_SELF') return t('tenant.invite.self');
  if (code === 'RESELLER_MEMBER_NOT_INVITABLE')
    return t('members.error.RESELLER_MEMBER_NOT_INVITABLE');
  return null;
}
